import { format, parseISO } from 'date-fns';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Line, LineChart, Tooltip } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { abbreviateNumber } from '@src/utils/data';
import { ChartTooltip } from '@Components/Charts/ChartTooltip/ChartTooltip';

const CHART_MARGIN = { top: 20, right: 30, left: 0, bottom: 10 };
const Y_AXIS_WIDTH = 40;

// abbreviateNumber only rounds values >= 1000 (to the k/m/g/t tier); below that it passes the
// raw number through unrounded, which can be a long floating-point tail (e.g. 66.14999999999998).
const formatValue = (value: number): string => {
  const abbreviated = abbreviateNumber(value);
  return typeof abbreviated === 'number' ? abbreviated.toFixed(2) : abbreviated;
};

interface AmcLineChartProps {
  data: Record<string, unknown>[];
  dataKey: string;
  lineColor: string;
  tooltipKey: string;
}

interface AmcLineChartTooltipProps {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
  lineColor: string;
  tooltipKey: string;
}

const AmcLineChartTooltip = ({
  active,
  payload,
  label,
  lineColor,
  tooltipKey
}: AmcLineChartTooltipProps) => {
  if (!active || !payload || payload.length === 0 || !label) return null;
  return (
    <ChartTooltip
      headerLabel={format(parseISO(label), 'MMM dd, yyyy')}
      items={[
        { label: tooltipKey, value: formatValue(Number(payload[0].value)), color: lineColor }
      ]}
    />
  );
};

export const AmcLineChart = ({ data, dataKey, lineColor, tooltipKey }: AmcLineChartProps) => (
  <AutoSizer
    renderProp={({ width = 0, height = 0 }) => (
      <LineChart data={data} width={width} height={height} margin={CHART_MARGIN}>
        <ChartGrid vertical={false} />
        <ChartXAxis dataKey='date' tickFormatter={value => format(parseISO(value), 'MMM dd')} />
        <ChartYAxis
          width={Y_AXIS_WIDTH}
          // Axis ticks are recharts' own auto-generated "nice" round numbers, unlike the
          // tooltip's raw data point — no need for formatValue's decimal truncation here.
          tickFormatter={tick => String(abbreviateNumber(Number(tick)))}
        />
        <Tooltip
          isAnimationActive={false}
          content={<AmcLineChartTooltip lineColor={lineColor} tooltipKey={tooltipKey} />}
        />
        <Line
          dataKey={dataKey}
          dot={false}
          stroke={lineColor}
          strokeWidth={3}
          type='monotone'
          isAnimationActive={false}
        />
      </LineChart>
    )}
  />
);
